import { defineStore } from 'pinia';
import type { FormSubmission, PortalFormRequest, TenantForm } from '~/types/models';
import { isOpenPortalFormRequest } from '~/lib/portalFormProgress';
import { toMillis } from '~/lib/timestamps';
import { buildClientPortalDataScopeKey, isCurrentClientPortalDataScope } from '~/lib/clientPortalDataScope';

type PortalFormDetailResponse = {
    form: TenantForm | null;
    request: PortalFormRequest | null;
    submission: FormSubmission | null;
};

function sortRequests(items: PortalFormRequest[]): PortalFormRequest[] {
    return [...items].sort((a, b) => {
        if (isOpenPortalFormRequest(a.status) && !isOpenPortalFormRequest(b.status)) return -1;
        if (!isOpenPortalFormRequest(a.status) && isOpenPortalFormRequest(b.status)) return 1;

        const aTime = a.status === 'completed'
            ? toMillis(a.completedAt || a.updatedAt || a.sentAt || a.createdAt)
            : toMillis(a.updatedAt || a.sentAt || a.createdAt);
        const bTime = b.status === 'completed'
            ? toMillis(b.completedAt || b.updatedAt || b.sentAt || b.createdAt)
            : toMillis(b.updatedAt || b.sentAt || b.createdAt);

        return bTime - aTime;
    });
}

export const useClientPortalFormsStore = defineStore('clientPortalForms', {
    state: () => ({
        requests: [] as PortalFormRequest[],
        currentRequest: null as PortalFormRequest | null,
        currentForm: null as TenantForm | null,
        currentSubmission: null as FormSubmission | null,
        scopeKey: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    getters: {
        pendingRequests: (state): PortalFormRequest[] => state.requests.filter((request) => isOpenPortalFormRequest(request.status)),
        completedRequests: (state): PortalFormRequest[] => state.requests.filter((request) => request.status === 'completed'),
        pendingCount(): number {
            return this.pendingRequests.length;
        },
        totalCount(): number {
            return this.requests.length;
        },
    },

    actions: {
        async _authHeaders(): Promise<Record<string, string>> {
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;

            if (!authUser) {
                throw new Error('You must be signed in to load portal forms.');
            }

            return {
                authorization: `Bearer ${await authUser.getIdToken()}`,
            };
        },

        _portalQuery(tenantId: string, clientId: string) {
            const portalStore = useClientPortalStore();
            return {
                tenantId,
                clientId,
                ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
            };
        },

        _activateScope(tenantId: string, clientId: string): string {
            const portalStore = useClientPortalStore();
            const scopeKey = buildClientPortalDataScopeKey({ tenantId, clientId, previewClientId: portalStore.previewingAsClientId });
            if (this.scopeKey !== scopeKey) {
                this.clear();
                this.scopeKey = scopeKey;
            }
            return scopeKey;
        },

        _upsertRequest(request: PortalFormRequest | null | undefined) {
            if (!request?.id) return;

            const next = [...this.requests];
            const index = next.findIndex((entry) => entry.id === request.id);
            if (index >= 0) {
                next[index] = request;
            } else {
                next.push(request);
            }
            this.requests = sortRequests(next);
        },

        applyRequest(request: PortalFormRequest | null | undefined) {
            if (!request) return;
            this.currentRequest = request;
            this._upsertRequest(request);
        },

        clear(): void {
            this.requests = [];
            this.currentRequest = null;
            this.currentForm = null;
            this.currentSubmission = null;
            this.loading = false;
            this.error = null;
            this.scopeKey = null;
        },

        async fetchForClient(tenantId: string, clientId: string): Promise<void> {
            const scopeKey = this._activateScope(tenantId, clientId);
            this.loading = true;
            this.error = null;

            try {
                const response = await $fetch<{ requests: PortalFormRequest[] }>('/api/client-portal/form-requests', {
                    query: this._portalQuery(tenantId, clientId),
                    headers: await this._authHeaders(),
                });
                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) {
                    this.requests = sortRequests(Array.isArray(response.requests) ? response.requests : []);
                }
            } catch (error) {
                if (!isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) return;
                this.error = `Error loading portal forms: ${error}`;
                console.error(this.error);
                this.requests = [];
            } finally {
                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) this.loading = false;
            }
        },

        async fetchDetail(options: {
            tenantId: string;
            clientId: string;
            requestOrSlug: string;
        }): Promise<PortalFormDetailResponse> {
            const scopeKey = this._activateScope(options.tenantId, options.clientId);
            this.loading = true;
            this.error = null;

            try {
                const response = await $fetch<PortalFormDetailResponse>(`/api/client-portal/forms/${encodeURIComponent(options.requestOrSlug)}`, {
                    query: this._portalQuery(options.tenantId, options.clientId),
                    headers: await this._authHeaders(),
                });

                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) {
                    this.currentRequest = response.request || null;
                    this.currentForm = response.form || null;
                    this.currentSubmission = response.submission || null;
                    this._upsertRequest(response.request);
                }

                return response;
            } catch (error) {
                if (!isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) throw error;
                this.error = `Error loading portal form: ${error}`;
                console.error(this.error);
                this.currentRequest = null;
                this.currentForm = null;
                this.currentSubmission = null;
                throw error;
            } finally {
                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) this.loading = false;
            }
        },

        async saveDraft(options: {
            tenantId: string;
            clientId: string;
            requestId: string;
            answers: PortalFormRequest['draftAnswers'];
            preview?: boolean;
        }): Promise<PortalFormRequest> {
            const scopeKey = this._activateScope(options.tenantId, options.clientId);
            const response = await $fetch<{ request: PortalFormRequest }>('/api/client-portal/forms/draft', {
                method: 'POST',
                headers: await this._authHeaders(),
                body: {
                    tenantId: options.tenantId,
                    clientId: options.clientId,
                    requestId: options.requestId,
                    answers: options.answers || [],
                    preview: options.preview === true,
                },
            });
            if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) {
                this.applyRequest(response.request);
            }
            return response.request;
        },
    },
});
